import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
  Checkbox,
  Divider,
  Pagination,
  CircularProgress,
  TextField,
  IconButton,
  Menu,
  MenuItem
} from "@mui/material";
import {
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  Description as ApplicationIcon,
  Business as UniversityIcon,
  Block as RestrictedIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import SmartPagination from "../../components/common/SmartPagination";
import CourseCard from "../../components/common/CourseCard";
import { getDegree } from "../../services/DegreeService";
import { getIntake } from "../../services/IntakeService";
import { getField } from "../../services/FieldService";
import { getApplication } from "../../services/ApplicationService";
import { getUniversity } from "../../services/UniversityService";
import { getCourse, getSavedCourse } from "../../services/CourseService";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { getCourseTypes } from "../../services/CourseTypeService";
import { getLanguages } from "../../services/CourseLanguageService";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Popper, ClickAwayListener } from "@mui/material";
import LoginQRModal from "../../components/LoginQRModal ";
import { useSelector } from "react-redux";
import { bookmarkCourseData } from "../../services/CourseService";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { useQuery } from "@tanstack/react-query";


const StudyProgramsSearch = () => {
  const resultsTopRef = useRef(null);
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [sidebarRef, sidebarInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [resultsRef, resultsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const slideLeft = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const slideRight = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const { auth } = useSelector((state) => state.auth || {});
  const isLoggedIn = Boolean(auth?.idToken || auth?.email);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);

  const [universitySearch, setUniversitySearch] = useState("");


  /** ---------------- RESULTS STATE ---------------- **/
  const [page, setPage] = useState(1);
  const limit = 10;
  const [courses, setCourses] = useState([]);
  const [totalCourses, setTotalCourses] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  /** ---------------- SIDEBAR STATES ---------------- **/
  const [openSection, setOpenSection] = useState(null);
  const handleToggle = (event, id) => {
    setAnchorEl(event.currentTarget);
    setOpenSection(openSection === id ? null : id);
  };

  const handleClickAway = (event) => {
    if (!openSection) return; // if already closed do nothing

    // ignore click on anchor button
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }

    setOpenSection(null);
    setAnchorEl(null);
  };

  const degreeLimit = 10;

  const [universityTypeOptions, setUniversityTypeOptions] = useState([{
    id: "public",
    title: "Public"
  }, {
    id: "private",
    title: "Private"
  }])

  /** Dynamic filter data (Degree example) **/
  const [anchorEl, setAnchorEl] = useState(null);
  const [courseTypeOptions, setCourseTypeOptions] = useState([]);
  const [courseTypePage, setCourseTypePage] = useState(1);
  const [hasMoreCourseTypes, setHasMoreCourseTypes] = useState(true);
  const [loadingCourseTypes, setLoadingCourseTypes] = useState(false);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [languagePage, setLanguagePage] = useState(1);
  const [hasMoreLanguages, setHasMoreLanguages] = useState(true);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  const [intakeOptions, setIntakeOptions] = useState([]);
  const [intakePage, setIntakePage] = useState(1);
  const [hasMoreIntakes, setHasMoreIntakes] = useState(true);
  const [loadingIntakes, setLoadingIntakes] = useState(false);

  const [fieldOptions, setFieldOptions] = useState([]);
  const [fieldPage, setFieldPage] = useState(1);
  const [hasMoreFields, setHasMoreFields] = useState(true);
  const [loadingFields, setLoadingFields] = useState(false);

  const [applicationOptions, setApplicationOptions] = useState([]);
  const [applicationPage, setApplicationPage] = useState(1);
  const [hasMoreApplications, setHasMoreApplications] = useState(true);
  const [loadingApplications, setLoadingApplications] = useState(false);

  const [universityOptions, setUniversityOptions] = useState([]);
  const [universityPage, setUniversityPage] = useState(1);
  const [hasMoreUniversities, setHasMoreUniversities] = useState(true);
  const [loadingUniversities, setLoadingUniversities] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const [shouldResetPage, setShouldResetPage] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    // degree_ids: [],
    coursetype_ids: [],
    language_ids: [],
    intake_ids: [],
    // field_ids: [],
    application_ids: [],
    university_ids: [],
    university_type: [],
    search: "",
  });

  const [selectedFilterLabels, setSelectedFilterLabels] = useState({
    // degree_ids: [],
    coursetype_ids: [],
    intake_ids: [],
    language_ids: [],
    // field_ids: [],
    application_ids: [],
    university_ids: [],
    university_type: [],
  });

  const navigate = useNavigate();


  const redirectDetailPage = (programid) => {
    localStorage.setItem("courseFilters", JSON.stringify(selectedFilters));
    localStorage.setItem("courseFilterLabels", JSON.stringify(selectedFilterLabels));
    localStorage.setItem("courseSearch", searchTerm);
    localStorage.setItem("courseShowSaved", JSON.stringify(showSaved));
    localStorage.setItem("coursePage", page);
    localStorage.setItem("selectedCourseId", programid);

    navigate(`/program/${programid}`)
  }


  const handleSearch = () => {
    setShouldResetPage(true);

    setSelectedFilters((prev) => ({
      ...prev,
      search: searchTerm, // store keyword in same filter object
    }));
    // setPage(1); // ✅ ONLY THIS
  };

  // useEffect(() => {
  //   fetchCourses(page, selectedFilters);
  // }, [page, selectedFilters, showSaved]);

  // useEffect(() => {
  //   if (!isRestored) return; // ⛔ don't reset during restore

  //   setPage(page);
  // }, [selectedFilters]);

  useEffect(() => {
    setPage(1);
  }, [showSaved, searchTerm]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setShouldResetPage(true);

      setSelectedFilters((prev) => ({
        ...prev,
        search: searchTerm,
      }));
    }, 500); // wait 0.5s after user stops typing

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    const savedFilters = localStorage.getItem("courseFilters");
    const savedLabels = localStorage.getItem("courseFilterLabels");
    const savedSearch = localStorage.getItem("courseSearch");
    const savedShowSaved = localStorage.getItem("courseShowSaved");
    const savedPage = localStorage.getItem("coursePage");

    if (savedFilters) setSelectedFilters(JSON.parse(savedFilters));
    if (savedLabels) setSelectedFilterLabels(JSON.parse(savedLabels));
    if (savedSearch) setSearchTerm(savedSearch);
    if (savedShowSaved) setShowSaved(JSON.parse(savedShowSaved));
    if (savedPage) setPage(Number(savedPage));

    setIsRestored(true); // ✅ important
  }, []);

  // Use TanStack Query for main search results
  const { data: searchResults, isFetching: searchLoading } = useQuery({
    queryKey: ["courses", page, selectedFilters, showSaved, isRestored],
    queryFn: async () => {
      if (!isRestored) return null;

      const payload = {
        ...selectedFilters,
        id: auth?.localId || null,
        page,
        limit,
      };

      let res;
      if (showSaved) {
        res = await getSavedCourse({
          ...selectedFilters,
          page,
          limit,
        });
      } else {
        res = await getCourse(payload);
      }
      return res.data;
    },
    enabled: isRestored,
    staleTime: 0, // IMPORTANT: Do not cache search results
    gcTime: 0,
  });

  // Sync search results with local state
  useEffect(() => {
    if (searchResults && searchResults.success) {
      setCourses(searchResults.data);
      setTotalCourses(searchResults.pagination.totalItems);
    }
  }, [searchResults]);

  // Sync loading state
  useEffect(() => {
    if (page === 1) setInitialLoading(searchLoading);
    else setLoadingMore(searchLoading);
  }, [searchLoading, page]);

  useEffect(() => {
    if (!isRestored) return; // ⛔ stop early

    if (shouldResetPage) {
      setPage(page);              // ✅ FIRST reset page
      setShouldResetPage(false);
      return;                  // ⛔ STOP API CALL THIS TIME
    }
  }, [page, selectedFilters, showSaved, isRestored, shouldResetPage]);

  useEffect(() => {
    const selectedId = localStorage.getItem("selectedCourseId");

    if (selectedId && courses.length > 0) {
      requestAnimationFrame(() => {
        const element = document.getElementById(`course-${selectedId}`);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }

        localStorage.removeItem("selectedCourseId");
      });
    }
  }, [courses]);

  /** ---------------- SIMULATED API CALLS ---------------- **/

  // Courses pagination API simulation
  const fetchCourses = async (pageNum, filters) => {
    try {
      if (pageNum === 1) setInitialLoading(true);
      else setLoadingMore(true);

      const payload = {
        ...filters,
        id: auth?.localId || null,
        page: pageNum,
        limit,
      };

      let res;

      if (showSaved) {
        res = await getSavedCourse({
          ...filters,
          page: pageNum,
          limit,
        });
      } else {
        res = await getCourse(payload);
      }

      if (res.data.success) {
        setCourses(res.data.data);

        setTotalCourses(res.data.pagination.totalItems);
      }
    } catch (error) {
      console.error("Fetch Courses Error:", error);
    } finally {
      setInitialLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (loadingMore || courses.length >= totalCourses) return;
    const nextPage = page + 1;
    setPage(nextPage);
  };

  // Degrees infinite scroll API simulation
  const fetchCourseTypes = async (pageNum = 1) => {
    if (loadingCourseTypes || !hasMoreCourseTypes) return;
    setLoadingCourseTypes(true);

    try {
      const res = await getCourseTypes(pageNum, degreeLimit);
      if (res.data.success == true) {
        const result = res.data;

        setCourseTypeOptions((prev) => [
          ...prev,
          ...result.data.map((item) => ({
            id: item.id,
            title: item.title,
          }))
        ]);

        setHasMoreCourseTypes(pageNum < result.pagination.totalPages);
        setCourseTypePage(pageNum);
      } else {
        setCourseTypeOptions([]);
        setHasMoreCourseTypes(false);
        setCourseTypePage(1);
      }
    } catch (err) {
      console.error("Course Type fetch error:", err);
    } finally {
      setLoadingCourseTypes(false);
    }
  };

  const fetchLanguages = async (pageNum = 1) => {
    if (loadingLanguages || !hasMoreLanguages) return;
    setLoadingLanguages(true);

    try {
      const res = await getLanguages(pageNum, degreeLimit);
      if (res.data.success == true) {
        const result = res.data;

        setLanguageOptions((prev) => [
          ...prev,
          ...result.data.map((item) => ({
            id: item.id,
            title: item.name,
          }))
        ]);

        setHasMoreLanguages(pageNum < result.pagination.totalPages);
        setLanguagePage(pageNum);
      } else {
        setLanguageOptions([]);
        setHasMoreLanguages(false);
        setLanguagePage(1);
      }
    } catch (err) {
      console.error("Course Language fetch error:", err);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const fetchIntakes = async (pageNum = 1) => {
    if (loadingIntakes || !hasMoreIntakes) return;
    setLoadingIntakes(true);

    try {
      const res = await getIntake(pageNum, degreeLimit);
      if (res.data.success == true) {
        const result = res.data;

        setIntakeOptions((prev) => [
          ...prev,
          ...result.data.map((item) => ({
            id: item.id,
            title: item.title,
          }))
        ]);

        setHasMoreIntakes(pageNum < result.pagination.totalPages);
        setIntakePage(pageNum);
      } else {
        setIntakeOptions([]);
        setHasMoreIntakes(false);
        setIntakePage(1);
      }
    } catch (err) {
      console.error("Intakes fetch error:", err);
    } finally {
      setLoadingIntakes(false);
    }
  };

  // const fetchFields = async (pageNum = 1) => {
  //   if (loadingFields || !hasMoreFields) return;
  //   setLoadingFields(true);

  //   try {
  //     const res = await getField(pageNum, degreeLimit);
  //     if (res.data.success == true) {
  //       const result = res.data;

  //       setFieldOptions((prev) => [
  //         ...prev,
  //         ...result.data.map((item) => ({
  //           id: item.id,
  //           title: item.title,
  //         }))
  //       ]);

  //       setHasMoreFields(pageNum < result.pagination.totalPages);
  //       setFieldPage(pageNum);
  //     } else {
  //       setFieldOptions([]);
  //       setHasMoreFields(false);
  //       setFieldPage(1);
  //     }
  //   } catch (err) {
  //     console.error("Intakes fetch error:", err);
  //   } finally {
  //     setLoadingFields(false);
  //   }
  // };

  const fetchApplications = async (pageNum = 1) => {
    if (loadingApplications || !hasMoreApplications) return;
    setLoadingApplications(true);

    try {
      const res = await getApplication(pageNum, degreeLimit);
      if (res.data.success == true) {
        const result = res.data;

        setApplicationOptions((prev) => [
          ...prev,
          ...result.data.map((item) => ({
            id: item.id,
            title: item.title,
          }))
        ]);

        setHasMoreApplications(pageNum < result.pagination.totalPages);
        setApplicationPage(pageNum);
      } else {
        setApplicationOptions([]);
        setHasMoreApplications(false);
        setApplicationPage(1);
      }
    } catch (err) {
      console.error("Intakes fetch error:", err);
    } finally {
      setLoadingApplications(false);
    }
  };

  const fetchUniversities = async (pageNum = 1) => {
    if (loadingUniversities || !hasMoreUniversities) return;
    setLoadingUniversities(true);

    try {
      const res = await getUniversity(pageNum, degreeLimit);
      if (res.data.success == true) {
        const result = res.data;
        setUniversityOptions((prev) => [
          ...prev,
          ...result.data.map((item) => ({
            id: item.id,
            title: item.title,
          }))
        ]);

        setHasMoreUniversities(pageNum < result.pagination.totalPages);
        setUniversityPage(pageNum);
      } else {
        setUniversityOptions([]);
        setHasMoreUniversities(false);
        setUniversityPage(1);
      }
    } catch (err) {
      console.error("Intakes fetch error:", err);
    } finally {
      setLoadingUniversities(false);
    }
  };

  // When sidebar "Degree" opens for the first time
  useEffect(() => {
    if (openSection === "coursetype" && courseTypeOptions.length === 0) {
      fetchCourseTypes(1);
    }
    if (openSection === "language" && languageOptions.length === 0) {
      fetchLanguages(1);
    }
    if (openSection === "intake" && intakeOptions.length === 0) {
      fetchIntakes(1);
    }
    // if (openSection === "fields" && fieldOptions.length === 0) {
    //   fetchFields(1);
    // }
    if (openSection === "application" && applicationOptions.length === 0) {
      fetchApplications(1);
    }
    if (openSection === "university" && universityOptions.length === 0) {
      fetchUniversities(1);
    }
  }, [openSection]);

  /** ---------------- FILTER SECTIONS ---------------- **/
  const filterSections = [
    { id: "coursetype", title: "Course Type", icon: <SchoolIcon />, dynamic: true },
    { id: "language", title: "Course Language", icon: <ApplicationIcon />, dynamic: true },
    { id: "intake", title: "Intake", icon: <CalendarIcon /> },
    // { id: "fields", title: "Fields", icon: <CategoryIcon /> },
    { id: "universitytype", title: "Type of University", icon: <UniversityIcon /> },
    { id: "application", title: "Application Type", icon: <ApplicationIcon /> },
    { id: "university", title: "Institution Name", icon: <UniversityIcon /> },
    // { id: "restricted", title: "Ad Restricted", icon: <RestrictedIcon /> },
  ];

  const fixBrokenUtf = (text = "") => {
    return text
      .replace(/Ã¼/g, "u")
      .replace(/Ã¶/g, "o")
      .replace(/Ã¤/g, "a")
      .replace(/ÃŸ/g, "ss")
      .replace(/Ã©/g, "e")
      .replace(/Ã/g, "a");
  };

  const normalizeText = (text = "") => {
    return fixBrokenUtf(text)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // remove accents
      .replace(/[^\w\s]/gi, "") // remove special chars
      .replace(/Ã/g, "A")
      .replace(/ü/gi, "u")
      .replace(/ö/gi, "o")
      .replace(/ä/gi, "a")
      .replace(/é/gi, "e")
      .replace(/ß/gi, "ss")
      .toLowerCase()
      .trim();
  };

  const handleFilterToggle = (key, id, title) => {
    setShouldResetPage(true);
    setSearchTerm("");
    setPage(1);

    setSelectedFilters((prev) => {
      const exists = prev[key].includes(id);
      return {
        ...prev,
        [key]: exists ? prev[key].filter((x) => x !== id) : [...prev[key], id],
      };
    });

    setSelectedFilterLabels((prev) => {
      const exists = prev[key].find((x) => x.id === id);
      return {
        ...prev,
        [key]: exists
          ? prev[key].filter((x) => x.id !== id)
          : [...prev[key], { id, title }],
      };
    });
    // setPage(1);
  };



  const hasFilters = Object.values(selectedFilterLabels).some(
    (filters) => filters.length > 0
  );

  const toggleBookmark = async (course) => {
    try {

      if (!isLoggedIn) {
        setLoginModalOpen(true);
        return;
      }

      const courseId = course.id || course._id;

      // Optimistic UI update
      setCourses((prevCourses) =>
        prevCourses.map((c) =>
          (c.id || c._id) === courseId
            ? { ...c, isBookmarked: !c.isBookmarked }
            : c
        )
      );

      // API call
      const response = await bookmarkCourseData(courseId);

      if (response.data.success == false) {
        throw new Error("Bookmark failed");
      }

      // Optional: sync with backend response
      setCourses((prevCourses) =>
        prevCourses.map((c) =>
          (c.id || c._id) === courseId
            ? { ...c, isBookmarked: response.data.isBookmarked }
            : c
        )
      );
    } catch (error) {
      console.error("Bookmark error:", error);

      // rollback on error
      setCourses((prevCourses) =>
        prevCourses.map((c) =>
          (c.id || c._id) === (course.id || course._id)
            ? { ...c, isBookmarked: course.isBookmarked }
            : c
        )
      );
    }
  };

  const handleClearFilters = () => {
    setShowSaved(false);
    setSelectedFilters({
      coursetype_ids: [],
      language_ids: [],
      intake_ids: [],
      application_ids: [],
      university_ids: [],
      university_type: [],
      search: "",
    });

    setSelectedFilterLabels({
      coursetype_ids: [],
      language_ids: [],
      intake_ids: [],
      application_ids: [],
      university_ids: [],
      university_type: [],
    });

    setSearchTerm("");

    // ✅ clear storage also
    localStorage.removeItem("courseFilters");
    localStorage.removeItem("courseFilterLabels");
    localStorage.removeItem("courseSearch");
    localStorage.removeItem("courseShowSaved");
    localStorage.removeItem("coursePage");

  };

  const handleToggleSaved = () => {
    if (!isLoggedIn) {
      setLoginModalOpen(true);
      return;
    }
    setShowSaved((prev) => !prev);

    setSelectedFilters({
      coursetype_ids: [],
      language_ids: [],
      intake_ids: [],
      application_ids: [],
      university_ids: [],
      university_type: [],
      search: "",
    });

    setSelectedFilterLabels({
      coursetype_ids: [],
      language_ids: [],
      intake_ids: [],
      application_ids: [],
      university_ids: [],
      university_type: [],
    });

    setSearchTerm("");

  };


  const filteredUniversities = universityOptions.filter((option) => {
    const normalizedTitle = normalizeText(option?.title || "");
    const normalizedSearch = normalizeText(universitySearch || "");

    return normalizedTitle.includes(normalizedSearch);
  });

  /** ---------------- RENDER ---------------- **/
  return (
    <>
      <Container maxWidth="xl" sx={{
        py: { xs: 2, sm: 2, md: 3 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        {/* ---------- HEADER ---------- */}
        <Box
          component={motion.div}
          ref={headerRef}
          variants={fadeUp}
          initial="hidden"
          animate={headerInView ? "visible" : "hidden"}
          sx={{ mb: { xs: 3, md: 3 }, width: "100%" }}>
          {/* Page Title */}

          <Box
            sx={{
              display: "inline-block",
              position: "relative",
              textAlign: "center",
              width: "100%",

              "&::after": {
                content: '""',
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: -5,
                width: "200px",
                height: "2px",
                backgroundColor: "#fd0000",
                opacity: "0.5"
              },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "24px", sm: "28px", md: "30px" },
                lineHeight: '1.3 !important',
                fontWeight: 700,
                color: "#000",
                textAlign: { xs: "left", md: "center" },
              }}
            >
              Study Programs Search
            </Typography>
          </Box>
          {/* Page Brief Description */}
          <Typography
            sx={{
              fontSize: { xs: "14px", md: "16px" },
              color: "#000",
              textAlign: { xs: "left", md: "center" },
              maxWidth: "700px",
              margin: "0 auto",
              mt: 2,
            }}
          >
            Find the right study program based on your interest, course type, language, intakes, Types of University,
            application Types, and more. Adjust filters to narrow down your perfect academic path.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              sx={{
                maxWidth: "600px",
                width: '100%',
                // margin: "0 auto",
                mb: 2,
                mt: 2,
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <TextField
                fullWidth
                placeholder="Search by program name, university, or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",

                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    paddingRight: "50px", // ✅ IMPORTANT (space for icon)

                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#002045",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#002045",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
              <IconButton
                onClick={handleSearch}
                sx={{
                  position: "absolute",
                  right: 8,
                  backgroundColor: "#002045",
                  color: "#fff",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#002045" },

                }}
              >
                <SearchIcon />
              </IconButton>
            </Box>

            <Box
              onClick={handleToggleSaved}
              sx={{
                display: 'flex',
                justifyItems: 'center',
                alignItems: 'center',
                px: 1,
                py: 1,
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: 'white',
                border: '1px solid gray',
              }}
            >
              {showSaved ? (
                <ListItemIcon sx={{ minWidth: 36, color: "#002045" }}>
                  <BookmarkIcon />
                </ListItemIcon>
              ) :
                <ListItemIcon sx={{ minWidth: 36, color: "#002045" }}>
                  <BookmarkBorderIcon />
                </ListItemIcon>
              }
              <ListItemText
                sx={{
                  display: { xs: "none", sm: "block" },
                  flex: 1,
                  minWidth: 0,
                }}
                primary={
                  <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "black", whiteSpace: 'nowrap !important' }}>
                    Saved Courses
                  </Typography>
                }
              />
            </Box>

          </Box>

          {/* Results & Applied Filters Overview */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: hasFilters ? "space-between" : "center",
              gap: 2,
              mb: 3,
            }}
          >
            {/* LEFT SIDE */}
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                fontWeight: 600,
                color: "#000",
                whiteSpace: "nowrap !important",
              }}
            >
              {hasFilters
                ? `${totalCourses} Results For Your Criteria`
                : `${totalCourses} Total Courses`}
            </Typography>

            {/* RIGHT SIDE */}
            {hasFilters && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flex: 1,
                  flexWrap: "wrap",
                }}
              >
                {/* LABEL */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#555",
                    whiteSpace: "nowrap",
                  }}
                >
                  Your Criteria:
                </Typography>

                {/* CHIPS CONTAINER */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    maxWidth: "100%",
                  }}
                >
                  {Object.entries(selectedFilterLabels).map(([key, filters]) =>
                    filters.map((item) => (
                      <Chip
                        key={item.id}
                        label={item.title}
                        size="small"
                        sx={{
                          backgroundColor: "#fd0000",
                          color: "#fff",
                          fontWeight: 500,
                          borderRadius: "20px",
                          fontSize: "12px",
                          height: "28px",
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* ---------- MAIN LAYOUT ---------- */}
        {/* ---------- SIDEBAR ---------- */}
        <Box
          component={motion.div}
          ref={sidebarRef}
          variants={slideLeft}
          initial="hidden"
          animate={sidebarInView ? "visible" : "hidden"}
        >
          <Paper
            elevation={0}
            sx={{
              pb: 2,
              borderRadius: "16px",
              transition: "height 0.3s ease",
            }}
          >
            <List sx={{ p: 0, display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto' }} >
              {filterSections.map((section) => (
                <Box
                  key={section.id}
                  sx={{
                    position: "relative",
                    flex: "0 0 auto",
                    minWidth: 170
                  }}
                >
                  <Box
                    onClick={(e) => handleToggle(e, section.id)}
                    sx={{
                      display: 'flex',
                      justifyItems: 'center',
                      alignItems: 'center',
                      px: 1,
                      py: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: 'white',
                      border: '1px solid gray',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: "#002045" }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "black", whiteSpace: 'nowrap' }}>
                          {section.title}
                        </Typography>
                      }
                    />
                    {openSection === section.id ? (
                      <ExpandLessIcon
                        sx={{
                          fontSize: "20px",
                          color: "#002045",
                          pointerEvents: "none", // 🔥 IMPORTANT
                        }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: "20px",
                          color: "#002045",
                          pointerEvents: "none", // 🔥 IMPORTANT
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
              {filterSections.length > 0 && (
                <Typography
                  sx={{
                    color: "#002045",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "none",
                    whiteSpace: 'nowrap !important'
                  }}
                  onClick={handleClearFilters}
                >
                  Clear Filter
                </Typography>
              )}

            </List>

            {/* coursetype */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "coursetype"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    minWidth: 220,
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    onScroll={(e) => {
                      const bottom =
                        Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                        e.target.scrollHeight - 1;

                      if (bottom && hasMoreCourseTypes) {
                        fetchCourseTypes(courseTypePage + 1);
                      }
                    }}
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      p: 1
                    }}
                  >
                    {!loadingCourseTypes && courseTypeOptions.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "black",
                          fontSize: "14px",
                        }}
                      >
                        No course types found
                      </Box>
                    )}
                    {courseTypeOptions && courseTypeOptions.length > 0 && [...courseTypeOptions]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id}
                          onClick={() => handleFilterToggle("coursetype_ids", option.id, option.title)}
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", alignItems: 'flex-start', p: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.coursetype_ids.includes(option.id)}
                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "black",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                    {loadingCourseTypes && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fd0000" }}
                        />
                      </Box>
                    )}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>

            {/* <LoginQRModal
          open={qrModelOpen}
          handleClose={() => setQrModelOpen(false)}
        /> */}

            {/* language */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "language"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    minWidth: 220,
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    onScroll={(e) => {
                      const bottom =
                        Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                        e.target.scrollHeight - 1;

                      if (bottom && hasMoreLanguages) {
                        fetchLanguages(languagePage + 1);
                      }
                    }}
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      p: 1
                    }}
                  >
                    {!loadingLanguages && languageOptions.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "black",
                          fontSize: "14px",
                        }}
                      >
                        No course languages found
                      </Box>
                    )}
                    {languageOptions && languageOptions.length > 0 && [...languageOptions]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id}
                          onClick={() => handleFilterToggle("language_ids", option.id, option.title)}
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", p: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.language_ids.includes(option.id)}
                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "black",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                    {loadingLanguages && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fd0000" }}
                        />
                      </Box>
                    )}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>

            {/* intake */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "intake"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    minWidth: 220,
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    onScroll={(e) => {
                      const bottom =
                        Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                        e.target.scrollHeight - 1;

                      if (bottom && hasMoreIntakes) {
                        fetchIntakes(intakePage + 1);
                      }
                    }}
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      p: 1
                    }}
                  >
                    {!loadingIntakes && intakeOptions.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "black",
                          fontSize: "14px",
                        }}
                      >
                        No intakes found
                      </Box>
                    )}
                    {intakeOptions && intakeOptions.length > 0 && [...intakeOptions]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id}
                          onClick={() =>
                            handleFilterToggle("intake_ids", option.id, option.title)
                          }
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", p: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.intake_ids.includes(option.id)}
                          // onChange={() => handleFilterToggle("intake_ids", option.id, option.title)}
                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "black",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                    {loadingIntakes && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fd0000" }}
                        />
                      </Box>
                    )}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>

            {/* universitytype */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "universitytype"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    minWidth: 220,
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      p: 1
                    }}
                  >
                    {universityTypeOptions && universityTypeOptions.length > 0 && [...universityTypeOptions]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id}
                          onClick={() => handleFilterToggle("university_type", option.id, option.title)}
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", p: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.university_type.includes(option.id)}

                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "black",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>

            {/* application */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "application"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    minWidth: 220,
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    onScroll={(e) => {
                      const bottom =
                        Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                        e.target.scrollHeight - 1;

                      if (bottom && hasMoreApplications) {
                        fetchApplications(applicationPage + 1);
                      }
                    }}
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      p: 1
                    }}
                  >
                    {!loadingApplications && applicationOptions.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "black",
                          fontSize: "14px",
                        }}
                      >
                        No applications found
                      </Box>
                    )}
                    {applicationOptions && applicationOptions.length > 0 && [...applicationOptions]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id}
                          onClick={() => handleFilterToggle("application_ids", option.id, option.title)}
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", p: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.application_ids.includes(option.id)}

                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "black",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                    {loadingApplications && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fd0000" }}
                        />
                      </Box>
                    )}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>

            {/* university */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "university"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    maxWidth: 300,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    // onScroll={(e) => {
                    //   const bottom =
                    //     Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                    //     e.target.scrollHeight - 1;

                    //   if (bottom && hasMoreUniversities) {
                    //     fetchUniversities(universityPage + 1);
                    //   }
                    // }}
                    sx={{
                      maxHeight: 250,
                      overflowY: "auto",
                      pt: 0,
                      pb: 1,
                      px: 1
                    }}
                  >
                    {!loadingUniversities && universityOptions.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "black",
                          fontSize: "14px",
                        }}
                      >
                        No universities found
                      </Box>
                    )}
                    <Box
                      sx={{
                        p: 1,
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search university..."
                        value={universitySearch}
                        onChange={(e) => setUniversitySearch(e.target.value)}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Box>
                    {universityOptions && filteredUniversities.length > 0 && [...filteredUniversities]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id}
                          onClick={() => handleFilterToggle("university_ids", option.id, option.title)}
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", padding: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.university_ids.includes(option.id)}
                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "black",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                    {loadingUniversities && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: "#002045" }}
                        />
                      </Box>
                    )}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>
          </Paper>
        </Box>

        <Divider sx={{ backgroundColor: "#002045", mb: 2, height: '1px' }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
            alignItems: "flex-start",
          }}
        >

          {/* ---------- RESULTS SECTION ---------- */}
          <Box
            component={motion.div}
            ref={(el) => {
              resultsRef(el);
              resultsTopRef.current = el;
            }}
            variants={slideRight}
            initial="hidden"
            animate={resultsInView ? "visible" : "hidden"}
            sx={{ flex: "1 1 70%", width: "100%" }}>
            {!initialLoading && courses.length === 0 && (
              <Typography sx={{ textAlign: "center", py: 4, color: "black" }}>
                No study programs found
              </Typography>
            )}
            {initialLoading ? (
              // 🔸 Show full-page loader only the first time
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 6,
                  minHeight: "400px", // ✅ give fixed height for smooth UX
                }}
              >
                <CircularProgress size={40} sx={{ color: "#002045" }} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 3, md: 4 },
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 2, md: 2 },
                    position: "relative",
                    opacity: loadingMore ? 0.5 : 1, // ✅ dim data while loading next
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {courses && [...courses]
                    .sort((a, b) =>
                      (a?.label || "").localeCompare(b?.label || "")
                    ).map((course) => (
                      <motion.div
                        key={course.id}
                        variants={fadeUp}
                        initial="hidden"
                        animate={resultsInView ? "visible" : "hidden"}
                        whileHover={{ y: -6 }}
                      >
                        <Box
                          id={`course-${course.id}`}
                          onContextMenu={(e) => {
                            e.preventDefault(); // ❌ stop default browser menu

                            setContextMenu({
                              mouseX: e.clientX,
                              mouseY: e.clientY,
                              courseId: course.id,
                            });
                          }}
                        >
                          <CourseCard
                            key={course.id}
                            course={course}
                            loading={loadingMore}
                            onClick={(e) => {
                              if (e.ctrlKey || e.metaKey) {
                                window.open(`/program/${course.id}`, "_blank");
                              } else {
                                redirectDetailPage(course.id);
                              }
                            }}
                            onBookmarkClick={(course) => toggleBookmark(course)}
                          />
                        </Box>
                      </motion.div>
                    ))}
                </Box>

                {/* ---------- Full overlay loader for smooth transition ---------- */}
                {loadingMore && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(255,255,255,0.6)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "16px",
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: "#002045" }} />
                  </Box>
                )}

                {/* ---------- PAGINATION ---------- */}
                <SmartPagination
                  count={Math.ceil(totalCourses / limit)}
                  page={page}
                  onChange={(e, value) => {
                    setPage(value);

                    setTimeout(() => {
                      resultsTopRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 100);
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {
          loginModalOpen && (
            <LoginQRModal
              open={loginModalOpen}
              handleClose={() => setLoginModalOpen(false)}
            />
          )
        }
      </Container >
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            redirectDetailPage(contextMenu.courseId);
            setContextMenu(null);
          }}
        >
          Open
        </MenuItem>
        <MenuItem
          onClick={() => {
            window.open(`/program/${contextMenu.courseId}`, "_blank");
            setContextMenu(null);
          }}
        >
          Open in new tab
        </MenuItem>
      </Menu>
    </>
  );
};

export default StudyProgramsSearch;
